import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
  FC,
  useContext,
} from "react";
import AuthContext from "./JWTContext";

interface Config {
  SuperAdminUsernames?: string[];
}

export interface ConfigContextValue {
  isAdminUser: boolean;
  loading: boolean;
  error: Error | null;
}

export const ConfigContext = createContext<ConfigContextValue>({
  isAdminUser: false,
  loading: true,
  error: null,
});

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider: FC<ConfigProviderProps> = ({ children }) => {
  const { user, isAuthenticated, isInitialized } = useContext(AuthContext);
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch("/config.json");
        if (!response.ok) throw new Error(`Failed to load config.json: ${response.statusText}`);
        const data = await response.json();
        setConfig(data);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const isAdminUser = useMemo(() => {
    if (!isAuthenticated || !user?.username || !config?.SuperAdminUsernames) {
      return false;
    }
    return config.SuperAdminUsernames.includes(user.username);
  }, [isAuthenticated, user?.username, config]);

  return (
    <ConfigContext.Provider
      value={{
        isAdminUser,
        loading: loading || !isInitialized,
        error,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};
